import { createRouter, createWebHistory, RouteLocationNormalized, RouteRecordRaw } from 'vue-router';
import { session } from './session';
import type { PlacementMap } from '@flagships/rails-frontend';
import { cardMeta } from './lib/cards';

export const SCREENS: Record<string, { title: string; icon: string }> = {
  flow: { title: 'Путь продаж', icon: 'pulse' },
  deals: { title: 'Сделки', icon: 'deals' },
  money: { title: 'Деньги', icon: 'money' },
  quality: { title: 'Качество лидов', icon: 'quality' },
  direct: { title: 'Обучение Директа', icon: 'direct' },
  sources: { title: 'Входы', icon: 'entry' },
  settings: { title: 'Настройки', icon: 'settings' },
};
/** Вкладки сверху. Остальные разделы открываются кнопками справа или из экранов, а в полосе подсвечивают свою вкладку. */
export const TABS = ['flow', 'deals', 'money'] as const;
export const TAB_OF: Record<string, string> = { quality: 'money', direct: 'flow' };
export const NAV_BY_ROLE: Record<string, string[]> = {
  owner: ['flow', 'deals', 'money', 'quality', 'settings'],
  marketer: ['flow', 'deals', 'money', 'quality', 'direct', 'sources', 'settings'],
  rop: ['flow', 'deals', 'money', 'quality', 'settings'],
  manager: ['deals', 'settings'],
  // Администратор портала в малом бизнесе и есть собственник: видит всё, начинает с «Пути продаж».
  admin: ['flow', 'deals', 'money', 'quality', 'direct', 'sources', 'settings'],
};
/**
 * До подключения Яндекса «Путь продаж» считается по CRM и показывает, где обрывается линия. Тем, кто настраивает,
 * ещё «Настройки» и разделы карточки, которые работают без Яндекса (у «Форм сайта в CRM» это входы).
 * Менеджер своих сделок до подключения не видит в потоке: у него список сделок.
 */
export function navFor(role: string, connected: boolean, card?: string): string[] {
  if (!connected) {
    if (['admin', 'marketer'].includes(role)) return ['flow', ...cardMeta(card).preConnect, 'settings'];
    return role === 'manager' ? ['deals', 'settings'] : ['flow'];
  }
  return NAV_BY_ROLE[role] ?? ['deals'];
}
export const PLACEMENTS: PlacementMap = {
  DEFAULT: () => '/app',
  CRM_DEAL_DETAIL_TAB: (o) => `/placement/deal-tab/${String(o.ID ?? '')}`,
  CRM_LEAD_DETAIL_TAB: (o) => `/placement/lead-tab/${String(o.ID ?? '')}`,
};
const VIEWS: Record<string, () => Promise<unknown>> = {
  flow: () => import('./views/FlowView.vue'),
  deals: () => import('./views/DealsView.vue'),
  sources: () => import('./views/SourcesView.vue'),
  settings: () => import('./views/SettingsView.vue'),
  direct: () => import('./views/DirectView.vue'),
  money: () => import('./views/MoneyView.vue'),
  quality: () => import('./views/QualityView.vue'),
};
export const routes: RouteRecordRaw[] = [
  { path: '/', redirect: '/launch' },
  { path: '/launch', component: () => import('./views/LaunchView.vue') },
  {
    path: '/app', component: () => import('./views/AppShell.vue'),
    children: [
      { path: '', component: () => import('./views/ScreenView.vue') },
      // Пульт и «Начало» стали «Путём продаж»: на старые адреса ведут сообщения бота и закладки.
      { path: 'pulse', redirect: (to) => ({ path: '/app/flow', query: to.query }) },
      { path: 'start', redirect: (to) => ({ path: '/app/flow', query: to.query }) },
      ...Object.keys(SCREENS).map((k) => ({ path: k, name: k, component: VIEWS[k] ?? (() => import('./views/ScreenView.vue')) })),
    ],
  },
  { path: '/placement/deal-tab/:id', component: () => import('./views/DealTabView.vue'), props: (r) => ({ id: r.params.id, entity: 'deal' }) },
  { path: '/placement/lead-tab/:id', component: () => import('./views/DealTabView.vue'), props: (r) => ({ id: r.params.id, entity: 'lead' }) },
  // Встройка приходит на /placement/<вид> без номера: номер знает только фрейм, его читает экран входа.
  { path: '/placement/:kind', redirect: (to) => ({ path: '/launch', query: to.query }) },
  { path: '/:rest(.*)*', redirect: '/launch' },
];
// Разделы без сессии не рисуются: иначе на мгновение видна оболочка с чужим набором разделов.
export function requireSession(hasSession: () => boolean) {
  return (to: RouteLocationNormalized) => (to.path.startsWith('/app') && !hasSession() ? { path: '/launch', query: to.query } : true);
}
export function buildRouter() {
  const router = createRouter({ history: createWebHistory(), routes });
  router.beforeEach(requireSession(() => !!session.token));
  return router;
}
